import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Share2, Eye, Edit3, ShieldAlert } from 'lucide-react';
import './WorkframeSharePromptModal.css';

// Diálogo que aparece cuando etiquetas (@) a alguien en una tarea estructurada pero esa persona
// no tiene acceso al documento vinculado (integración main→dev, epic D1.3). Presentacional: el
// contenedor decide qué hace `onGrantAccess({view, edit})` (el port directo del blob
// `doc_permissions` — decisión del usuario 2026-09-09, ver D1-workframe.md §0.4).

export function WorkframeSharePromptModal({
    isOpen,
    onClose,
    targetUser,
    docTitle,
    onGrantAccess,
    isSubmitting = false,
}) {
    const { t } = useTranslation('tasks');

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isSubmitting) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, isSubmitting]);

    if (!isOpen || !targetUser) return null;

    const userName = targetUser.nick || targetUser.name || t('workframe.bitacora.user');
    const avatarUrl = targetUser.avatar
        || targetUser['link-profile-img-thumb']
        || targetUser['link-profile-img']
        || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}`;

    return createPortal(
        <div className="wf-share-modal-overlay" onClick={(e) => e.target === e.currentTarget && !isSubmitting && onClose()}>
            <div className="wf-share-modal-panel" role="dialog" aria-modal="true">
                <div className="wf-share-modal-header">
                    <div className="wf-share-modal-title-box">
                        <Share2 size={15} color="#60a5fa" />
                        <span className="wf-share-modal-title">{t('workframe.share.title')}</span>
                    </div>
                    <button
                        type="button"
                        className="wf-share-modal-close"
                        onClick={onClose}
                        disabled={isSubmitting}
                        aria-label={t('workframe.share.close')}
                    >
                        <X size={15} />
                    </button>
                </div>

                <div className="wf-share-modal-body">
                    <div className="wf-share-user-card">
                        <img src={avatarUrl} alt={userName} className="wf-share-avatar" />
                        <div className="wf-share-user-details">
                            <span className="wf-share-user-name">@{userName}</span>
                            <span className="wf-share-user-status">
                                <ShieldAlert size={12} className="wf-share-status-icon" />
                                {t('workframe.share.noAccess')}
                            </span>
                        </div>
                    </div>

                    <p className="wf-share-message">
                        {t('workframe.share.message', {
                            name: userName,
                            title: docTitle || t('workframe.share.linkedDocFallback'),
                        })}
                    </p>

                    <div className="wf-share-actions">
                        <div className="wf-share-btn-row">
                            <button
                                type="button"
                                className="wf-share-btn wf-share-btn-view"
                                disabled={isSubmitting}
                                onClick={() => onGrantAccess({ view: true, edit: false })}
                            >
                                <Eye size={14} />
                                <span>{t('workframe.share.readOnly')}</span>
                            </button>
                            <button
                                type="button"
                                className="wf-share-btn wf-share-btn-edit"
                                disabled={isSubmitting}
                                onClick={() => onGrantAccess({ view: true, edit: true })}
                            >
                                <Edit3 size={14} />
                                <span>{t('workframe.share.editor')}</span>
                            </button>
                        </div>
                        <button
                            type="button"
                            className="wf-share-btn wf-share-btn-skip"
                            disabled={isSubmitting}
                            onClick={onClose}
                        >
                            {t('workframe.share.skip')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default WorkframeSharePromptModal;
